import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Table, Button, Tag, Space, Input, message, Modal, Select, DatePicker } from 'antd';
import { PlusOutlined, SearchOutlined, InboxOutlined, UndoOutlined, AuditOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PDFDownloadLink } from '@react-pdf/renderer';
import StatementPDF from './StatementPDF';
import dayjs from 'dayjs';
import API_URL from '../config/api';
import { apiFetch } from '../utils/apiFetch';
import { ListPagination, paginateList, LIST_PAGE_SIZE } from './ListPagination';
import './CustomerList.css';

function getCustomerType(c: Customer & { customer_type?: string }): string {
  return c.customerType || c.customer_type || 'Retail';
}

function customerTypeTagColor(type: string): string {
  if (type === 'Wholesale') return 'blue';
  if (type === 'Walk-in') return 'orange';
  return 'default';
}

interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  companyName?: string;
  website?: string;
  tags?: string[];
  notes?: string;
  status?: string;
  customerType?: string;
  credit_terms?: number;
  business_id?: string;
  created_at?: string;
  created_by?: string;
  lastInteraction?: string;
  totalPurchases?: number;
  creditLimit?: number;
  paymentTerms?: string;
  assignedTo?: string;
  marketingPreferences?: {
    email?: boolean;
    sms?: boolean;
    phone?: boolean;
  };
  customerSegment?: 'VIP' | 'Regular' | 'New' | 'Inactive';
  socialMedia?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
  };
  image?: string | null;
  deleted_at?: string | null;
  deleted_by?: string | null;
  last_order_date?: string | null;
  total_spend?: number | string;
  outstanding_balance?: number | string;
  portal_status?: 'active' | 'invited' | 'invite_expired' | 'not_invited';
  portal_invited_at?: string | null;
}

function portalStatusLabel(status?: string): string {
  if (status === 'active') return 'Portal active';
  if (status === 'invited') return 'Invited';
  if (status === 'invite_expired') return 'Invite expired';
  return 'Not invited';
}

function portalStatusColor(status?: string): string {
  if (status === 'active') return 'green';
  if (status === 'invited') return 'blue';
  if (status === 'invite_expired') return 'orange';
  return 'default';
}

function numVal(v: unknown): number {
  return Number(v || 0);
}

// Small Avatar helper used for fallback (initials) and onError handling
const Avatar: React.FC<{ image?: string | null; name?: string }> = ({ image, name }) => {
  const [errored, setErrored] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Try to add Authorization header for protected images
  useEffect(() => {
    if (imgRef.current && image && !image.startsWith('http')) {
      try {
        imgRef.current.setAttribute('crossorigin', 'anonymous');
      } catch {}
    }
  }, [image]);
  const initials = (name || '')
    .split(' ')
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'CU';

  if (image && !errored) {
    // Only prepend API_URL if image is a relative path (not starting with http, https, or //)
    const isAbsolute = /^(https?:)?\/\//i.test(image);
    const src = isAbsolute ? image : `${API_URL}${image}`;
    return (
      <img
        ref={imgRef}
        src={src}
        alt={name}
        onError={() => setErrored(true)}
        style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
      />
    );
  }

  // Always show fallback if errored or no image
  return (
    <div style={{
      width: 40,
      height: 40,
      borderRadius: '50%',
      background: '#e3f2fd',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      color: '#0074d9'
    }}>
      <span style={{ fontSize: 20 }}>{initials || '👤'}</span>
    </div>
  );
};

const CustomerList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [segmentFilter] = useState<string | undefined>();
  const [typeFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [listPage, setListPage] = useState(1);
  const [invitingId, setInvitingId] = useState<string | null>(null);

  // Statement Modal State
  const [statementModalVisible, setStatementModalVisible] = useState(false);
  const [statementDateRange, setStatementDateRange] = useState<[any, any]>([
    dayjs().startOf('month'),
    dayjs().endOf('day')
  ]);
  const [statementData, setStatementData] = useState<any>(null);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [businessSettings, setBusinessSettings] = useState<any>(null);

  useEffect(() => {
    // Load business settings for PDF header
    const loadSettings = async () => {
      try {
        const res = await apiFetch(`${API_URL}/business`, {
          headers: { 'Authorization': `Bearer ${auth.token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setBusinessSettings(data);
        }
      } catch (e) {
        console.error('Failed to load business settings:', e);
      }
    };
    if (auth.token) loadSettings();
  }, [auth.token]);

  const fetchStatementData = async () => {
    if (!selectedCustomer) return;
    if (!statementDateRange?.[0] || !statementDateRange?.[1]) {
      message.warning('Please select a valid date range');
      return;
    }
    setLoadingStatement(true);
    setStatementData(null);
    try {
      const start = statementDateRange[0].format('YYYY-MM-DD');
      const end = statementDateRange[1].format('YYYY-MM-DD');
      const res = await apiFetch(`${API_URL}/customers/${selectedCustomer.id}/statement?start=${start}&end=${end}`, {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStatementData({
          ...data,
          transactions: Array.isArray(data.transactions) ? data.transactions : [],
        });
      } else {
        const err = await res.json().catch(() => null);
        message.error(err?.error || 'Failed to fetch statement data');
      }
    } catch (e) {
      console.error(e);
      message.error('Error loading statement');
    } finally {
      setLoadingStatement(false);
    }
  };

  const exportCustomers = () => {
    if (!customers || customers.length === 0) {
      message.warning('No customers to export');
      return;
    }
    const data = customers.map(c => ({
      'Name': c.name,
      'Email': c.email,
      'Phone': c.phone || '',
      'Company': c.companyName || '',
      'Type': c.customerType || '',
      'Segment': c.customerSegment || '',
      'Status': c.status || '',
      'Total Purchases': c.totalPurchases || 0,
      'Created At': c.created_at ? new Date(c.created_at).toLocaleDateString() : ''
    }));

    const csvContent = "data:text/csv;charset=utf-8," + 
      Object.keys(data[0]).join(",") + "\n" +
      data.map(row => Object.values(row).join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "customers.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // fetchCustomers is declared below and reused; call it when component mounts
  const searchTimeoutRef = useRef<number | null>(null);
  const csvImportRef = useRef<HTMLInputElement>(null);
  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  const [csvImportRows, setCsvImportRows] = useState<any[]>([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvImportErrors, setCsvImportErrors] = useState<Array<{ row: number; error: string }>>([]);

  const handleCsvImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const text = (ev.target?.result as string) || '';
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { message.warning('CSV must have a header row + at least one data row'); return; }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      const rows = lines.slice(1).map(line => {
        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        const obj: Record<string, string> = {};
        headers.forEach((h, idx) => { obj[h] = cols[idx] || ''; });
        return obj;
      });
      setCsvImportRows(rows);
      setCsvImportErrors([]);
      setShowCsvImportModal(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const submitCsvCustomerImport = async () => {
    setCsvImporting(true);
    setCsvImportErrors([]);
    try {
      const res = await apiFetch(`${API_URL}/customers/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ customers: csvImportRows }),
      });
      const data = res ? await res.json() : null;
      if (res?.ok) {
        setShowCsvImportModal(false);
        setCsvImportRows([]);
        message.success(`Imported ${data.imported} customer${data.imported !== 1 ? 's' : ''} successfully!`);
        fetchCustomers();
      } else {
        setCsvImportErrors(data?.errors || [{ row: 0, error: data?.error || 'Import failed' }]);
      }
    } catch {
      setCsvImportErrors([{ row: 0, error: 'Network error' }]);
    } finally {
      setCsvImporting(false);
    }
  };

  useEffect(() => {
    setListPage(1);
    fetchCustomers();
  }, [navigate, statusFilter]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (val) setIsSearching(true);
    
    // debounce server-side search
    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = window.setTimeout(() => {
      fetchCustomers(val);
    }, 500);
  };

  // Show flash message passed via navigation state (e.g., from CustomerForm)
  useEffect(() => {
    const flash = (location.state as any)?.flash;
    if (flash) {
      message.success(flash);
      // Clear the state so the message doesn't show again on navigation
      try {
        navigate(location.pathname, { replace: true, state: {} });
      } catch (e) {
        // ignore
      }
    }
  }, [location, navigate]);

  const fetchCustomers = async (q?: string) => {
    try {
      const token = auth.token;
      if (!token) {
        navigate('/login');
        return;
      }

      const params: Record<string, string> = {};
      if (statusFilter === 'Archived') params.include_archived = 'true';
      if (q && q.trim() !== '') params.q = q.trim();

      const qs = Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : '';
      const url = `${API_URL}/customers${qs}`;

      const response = await apiFetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setCustomers(list);
    } catch (error) {
      message.error('Failed to fetch customers');
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const handleArchive = async (id: string) => {
    if (!window.confirm('Are you sure you want to archive this customer? You can restore them later.')) return;

    try {
      const token = auth.token;
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await apiFetch(`${API_URL}/customers/${id}/archive`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        message.success('Customer archived successfully');
        fetchCustomers();
      } else {
        throw new Error('Failed to archive customer');
      }
    } catch (error) {
      message.error('Error archiving customer');
      console.error('Error:', error);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const token = auth.token;
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await apiFetch(`${API_URL}/customers/${id}/restore`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        message.success('Customer restored successfully');
        fetchCustomers();
      } else {
        throw new Error('Failed to restore customer');
      }
    } catch (error) {
      message.error('Error restoring customer');
      console.error('Error:', error);
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/customers/edit/${id}`, { state: { mode: 'edit' } });
  };

  const handleView = (id: string) => {
    navigate(`/customers/view/${id}`);
  };

  const handlePortalInvite = async (customer: Customer) => {
    if (!customer.email) {
      message.warning('Add an email address before sending a portal invite');
      return;
    }
    setInvitingId(customer.id);
    try {
      const res = await apiFetch(`${API_URL}/customers/${customer.id}/portal-invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        message.success(data.message || 'Portal invitation sent');
        fetchCustomers(searchTerm);
      } else {
        message.error(data.error || 'Failed to send invitation');
      }
    } catch {
      message.error('Failed to send invitation');
    } finally {
      setInvitingId(null);
    }
  };

  const columns: ColumnsType<Customer> = [
    {
      title: 'Customer',
      key: 'customer',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (_: any, record: Customer) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar image={record.image} name={record.name} />
          <div>
            <div style={{ fontWeight: 700, color: '#111827', fontSize: 14 }}>{record.name}</div>
            <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
              <Tag color={customerTypeTagColor(getCustomerType(record))} style={{ margin: 0, fontSize: 11 }}>
                {getCustomerType(record)}
              </Tag>
              {record.deleted_at && <Tag color="red" style={{ margin: 0, fontSize: 11 }}>ARCHIVED</Tag>}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div className="cust-meta">
          <div>{record.email}</div>
          <div className="cust-meta-muted">{record.phone || '—'}</div>
          {record.companyName && <div className="cust-meta-muted">{record.companyName}</div>}
        </div>
      ),
    },
    {
      title: 'Terms',
      key: 'credit_terms',
      sorter: (a, b) => (a.credit_terms || 0) - (b.credit_terms || 0),
      render: (_: unknown, record: Customer) => {
        const days = record.credit_terms;
        if (!days) return <Tag style={{ fontSize: 12 }}>Not set</Tag>;
        return (
          <Tag color={days <= 7 ? 'green' : days <= 30 ? 'blue' : 'orange'} style={{ fontSize: 12 }}>
            Net {days}
          </Tag>
        );
      },
    },
    {
      title: 'Total Spend',
      key: 'totalSpend',
      sorter: (a, b) => numVal(a.total_spend) - numVal(b.total_spend),
      render: (_, record) => {
        const spend = numVal(record.total_spend);
        return (
          <span className={spend > 0 ? 'cust-spend' : 'cust-spend cust-spend--empty'}>
            £{spend.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        );
      },
    },
    {
      title: 'Outstanding',
      key: 'outstanding',
      sorter: (a, b) => numVal(a.outstanding_balance) - numVal(b.outstanding_balance),
      render: (_, record) => {
        const bal = numVal(record.outstanding_balance);
        return bal > 0
          ? <span className="cust-outstanding">£{bal.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
          : <span className="cust-clear">Clear</span>;
      },
    },
    {
      title: 'Portal',
      key: 'portal',
      render: (_, record) => (
        <Tag color={portalStatusColor(record.portal_status)} style={{ fontSize: 12 }}>
          {portalStatusLabel(record.portal_status)}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const s = status || 'Active';
        return <Tag color={s === 'Active' ? 'green' : s === 'Inactive' ? 'orange' : 'gray'}>{s}</Tag>;
      },
    },
    {
      title: 'Last Order',
      dataIndex: 'last_order_date',
      key: 'last_order_date',
      sorter: (a, b) => {
        if (!a.last_order_date) return 1;
        if (!b.last_order_date) return -1;
        return new Date(a.last_order_date).getTime() - new Date(b.last_order_date).getTime();
      },
      render: (date: string | null) => date
        ? <span style={{ fontSize: 13 }}>{new Date(date).toLocaleDateString('en-GB')}</span>
        : <span style={{ color: '#9ca3af', fontSize: 13 }}>Never</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <div className="cust-row-actions">
          {record.deleted_at ? (
            <button type="button" className="cust-act cust-act--restore" onClick={() => handleRestore(record.id)}>Restore</button>
          ) : (
            <>
              <button type="button" className="cust-act cust-act--view" onClick={() => handleView(record.id)}>View</button>
              <button type="button" className="cust-act cust-act--edit" onClick={() => handleEdit(record.id)}>Edit</button>
              <button type="button" className="cust-act cust-act--invoices" onClick={() => navigate(`/invoices/list?customer_id=${record.id}&business_id=${record.business_id || auth.business_id}`)}>Invoices</button>
              <button type="button" className="cust-act cust-act--orders" onClick={() => navigate(`/orders/list?customer_id=${record.id}&business_id=${record.business_id || auth.business_id}`)}>Orders</button>
              <button type="button" className="cust-act cust-act--statement" onClick={() => { setSelectedCustomer(record); setStatementData(null); setStatementModalVisible(true); }}>Statement</button>
              {record.email && record.portal_status !== 'active' && (
                <button
                  type="button"
                  className="cust-act cust-act--portal"
                  disabled={invitingId === record.id}
                  onClick={() => handlePortalInvite(record)}
                >
                  {invitingId === record.id ? 'Sending…' : record.portal_status === 'invited' ? 'Resend invite' : 'Invite portal'}
                </button>
              )}
              <button type="button" className="cust-act cust-act--archive" onClick={() => handleArchive(record.id)}>Archive</button>
            </>
          )}
        </div>
      ),
    },
  ];

  const filteredCustomers = customers
    .filter(c => {
      // Segment filter
      if (segmentFilter && c.customerSegment !== segmentFilter) return false;

      // Type filter
      if (typeFilter && c.customerType !== typeFilter) return false;

      // Status filter
      if (statusFilter !== 'All') {
        if (statusFilter === 'Archived' && !c.deleted_at) return false;
        if (statusFilter === 'Active' && (c.deleted_at || c.status !== 'Active')) return false;
        if (statusFilter === 'Inactive' && (c.deleted_at || c.status !== 'Inactive')) return false;
      }

      return true;
    })
    // Client-side search fallback: filter the already-loaded list immediately while server search runs
    .filter(c => {
      if (!searchTerm || searchTerm.trim() === '') return true;
      const s = searchTerm.toLowerCase().trim();
      return (
        (c.name && c.name.toLowerCase().includes(s)) ||
        (c.email && c.email.toLowerCase().includes(s)) ||
        (c.phone && c.phone.toLowerCase().includes(s)) ||
        (c.companyName && c.companyName.toLowerCase().includes(s))
      );
    });

  const { pages, safePage, slice: pagedCustomers } = paginateList(filteredCustomers, listPage);

  return (
    <div className="cust-list-page">
      <div className="cust-list-header">
        <div>
          <h1 className="cust-list-title"><span className="cust-list-icon" aria-hidden="true">👤</span> Customers</h1>
          <p className="cust-list-subtitle">All your trade accounts — contacts, credit, and balances at a glance</p>
        </div>
        <Link to="/customers/add">
          <Button type="primary" icon={<PlusOutlined />}>Add customer</Button>
        </Link>
      </div>

      <div className="cust-stats">
        {[
          { label: 'Total', value: filteredCustomers.length, tone: 'blue' as const },
          { label: 'Active', value: filteredCustomers.filter(c => !c.deleted_at && c.status !== 'Inactive').length, tone: 'red' as const },
          { label: 'With balance', value: filteredCustomers.filter(c => numVal(c.outstanding_balance) > 0).length, tone: 'white' as const },
          { label: 'Outstanding', value: `£${filteredCustomers.reduce((s, c) => s + numVal(c.outstanding_balance), 0).toLocaleString('en-GB', { minimumFractionDigits: 0 })}`, tone: 'teal' as const },
        ].map((stat, i) => (
          <div key={i} className={`cust-kpi-card cust-kpi-card--${stat.tone}`}>
            <div className="cust-kpi-label">{stat.label}</div>
            <div className="cust-kpi-value">{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="cust-toolbar">
        <Space wrap>
          <Input
            placeholder="Search name, email, phone, company…"
            prefix={<SearchOutlined />}
            suffix={isSearching ? <span style={{ color: '#9ca3af', fontSize: 12 }}>…</span> : null}
            value={searchTerm}
            onChange={handleSearchChange}
            onPressEnter={() => fetchCustomers(searchTerm)}
            style={{ width: 280 }}
            allowClear
          />
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 140 }}>
            <Select.Option value="All">All</Select.Option>
            <Select.Option value="Active">Active</Select.Option>
            <Select.Option value="Inactive">Inactive</Select.Option>
            <Select.Option value="Archived">Archived</Select.Option>
          </Select>
        </Space>
        <Space wrap>
          <input ref={csvImportRef} type="file" accept=".csv" onChange={handleCsvImportFile} style={{ display: 'none' }} />
          <Button onClick={() => csvImportRef.current?.click()}>Import CSV</Button>
          <Button onClick={exportCustomers} icon={<DownloadOutlined />}>Export</Button>
        </Space>
      </div>

      <ListPagination
        total={customers.length}
        filtered={filteredCustomers.length}
        page={safePage}
        pages={pages}
        onPageChange={setListPage}
        noun="customers"
      />

      <div className="cust-table-panel">
        <Table
          columns={columns}
          dataSource={pagedCustomers}
          rowKey="id"
          rowClassName="compact-row"
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No customers found — add your first trade account' }}
          scroll={{ x: 980 }}
        />
      </div>

      <div className="cust-mobile-cards">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        ) : filteredCustomers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>No customers found</div>
        ) : (
          pagedCustomers.map(customer => (
            <div key={customer.id} className="cust-mobile-card">
              <div className="cust-mobile-card-header">
                <Avatar image={customer.image} name={customer.name} />
                <div style={{ flex: 1 }}>
                  <div className="cust-mobile-card-name">{customer.name}</div>
                  <Space size={4} wrap>
                    <Tag color={customerTypeTagColor(getCustomerType(customer))}>{getCustomerType(customer)}</Tag>
                    <Tag color={portalStatusColor(customer.portal_status)}>{portalStatusLabel(customer.portal_status)}</Tag>
                    {customer.deleted_at && <Tag color="red">Archived</Tag>}
                  </Space>
                </div>
              </div>

              <div className="cust-mobile-card-info">
                <div>{customer.email}</div>
                <div>{customer.phone || 'No phone'}</div>
                {customer.companyName && <div>{customer.companyName}</div>}
                <div>
                  Outstanding:{' '}
                  {numVal(customer.outstanding_balance) > 0
                    ? <span className="cust-outstanding">£{numVal(customer.outstanding_balance).toLocaleString('en-GB')}</span>
                    : <span className="cust-clear">Clear</span>}
                </div>
              </div>

              <div className="cust-mobile-actions cust-row-actions">
                {customer.deleted_at ? (
                  <button type="button" className="cust-act cust-act--restore" onClick={() => handleRestore(customer.id)}>Restore</button>
                ) : (
                  <>
                    <button type="button" className="cust-act cust-act--view" onClick={() => handleView(customer.id)}>View</button>
                    <button type="button" className="cust-act cust-act--edit" onClick={() => handleEdit(customer.id)}>Edit</button>
                    <button type="button" className="cust-act cust-act--invoices" onClick={() => navigate(`/invoices/list?customer_id=${customer.id}&business_id=${customer.business_id || auth.business_id}`)}>Invoices</button>
                    <button type="button" className="cust-act cust-act--orders" onClick={() => navigate(`/orders/list?customer_id=${customer.id}&business_id=${customer.business_id || auth.business_id}`)}>Orders</button>
                    <button type="button" className="cust-act cust-act--statement" onClick={() => { setSelectedCustomer(customer); setStatementData(null); setStatementModalVisible(true); }}>Statement</button>
                    {customer.email && customer.portal_status !== 'active' && (
                      <button
                        type="button"
                        className="cust-act cust-act--portal"
                        disabled={invitingId === customer.id}
                        onClick={() => handlePortalInvite(customer)}
                      >
                        {invitingId === customer.id ? 'Sending…' : 'Invite portal'}
                      </button>
                    )}
                    <button type="button" className="cust-act cust-act--archive" onClick={() => handleArchive(customer.id)}>Archive</button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {!loading && filteredCustomers.length > 0 && (
        <ListPagination
          total={customers.length}
          filtered={filteredCustomers.length}
          page={safePage}
          pages={pages}
          onPageChange={setListPage}
          noun="customers"
        />
      )}

      {/* Booking Modal */}
      {/* Statement Generation Modal */}
      <Modal
        title={<span><AuditOutlined /> Statement of Account</span>}
        open={statementModalVisible}
        onCancel={() => {
          setStatementModalVisible(false);
          setStatementData(null);
        }}
        footer={null}
        width={600}
      >
        <div style={{ padding: '10px 0' }}>
          <p>Generate a branded Statement of Account for <strong>{selectedCustomer?.name}</strong>.</p>
          
          <div style={{ marginBottom: '20px', marginTop: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Select Statement Period:</label>
            <DatePicker.RangePicker 
              style={{ width: '100%' }}
              value={statementDateRange}
              onChange={(dates: any) => dates && setStatementDateRange([dates[0], dates[1]])}
              presets={[
                { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                { label: 'Last Month', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                { label: 'Last 90 Days', value: [dayjs().subtract(90, 'days'), dayjs()] },
                { label: 'Year to Date', value: [dayjs().startOf('year'), dayjs()] },
              ]}
            />
          </div>

          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Transaction preview</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
                  {statementData
                    ? `${statementData.transactions?.length ?? 0} transactions · closing £${Number(statementData.closing_balance || 0).toFixed(2)}`
                    : 'Click fetch to preview'}
                </div>
              </div>
              <Button type="primary" onClick={fetchStatementData} loading={loadingStatement}>
                Fetch data
              </Button>
            </div>
            {statementData && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, fontSize: 12 }}>
                <div><span style={{ color: '#6b7280' }}>Opening</span><div style={{ fontWeight: 700 }}>£{Number(statementData.opening_balance || 0).toFixed(2)}</div></div>
                <div><span style={{ color: '#6b7280' }}>Invoiced</span><div style={{ fontWeight: 700 }}>£{Number(statementData.totals?.invoiced || 0).toFixed(2)}</div></div>
                <div><span style={{ color: '#6b7280' }}>Paid</span><div style={{ fontWeight: 700, color: '#059669' }}>£{Number(statementData.totals?.paid || 0).toFixed(2)}</div></div>
              </div>
            )}
          </div>

          {statementData && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '20px', textAlign: 'center' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <PDFDownloadLink
                  document={<StatementPDF data={statementData} businessSettings={businessSettings} />}
                  fileName={`Statement_${selectedCustomer?.name.replace(/\s+/g, '_')}_${statementDateRange[0].format('YYYY-MM-DD')}.pdf`}
                  style={{
                    textDecoration: 'none',
                    padding: '12px 24px',
                    color: '#fff',
                    backgroundColor: '#10b981',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    display: 'inline-block',
                    width: '100%',
                    textAlign: 'center'
                  }}
                >
                  {({ loading }) => (loading ? <span>📄 Preparing PDF...</span> : <span>📥 Download Statement PDF</span>)}
                </PDFDownloadLink>
                
                <Button 
                  icon={<InboxOutlined />} 
                  style={{ width: '100%', height: '45px', marginTop: '10px' }}
                  onClick={() => {
                    message.info('Email statement feature coming in Phase 5!');
                  }}
                >
                  Send to Customer via Email
                </Button>
              </Space>
            </div>
          )}
        </div>
      </Modal>

      {/* CSV Import Modal */}
      <Modal
        title="📥 Import Customers from CSV"
        open={showCsvImportModal}
        onCancel={() => { setShowCsvImportModal(false); setCsvImportRows([]); setCsvImportErrors([]); }}
        footer={null}
        width={700}
      >
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>
          Expected columns: <code>name, email, phone, company_name, customer_type</code>
        </p>
        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
          Only <code>name</code> is required. Existing customers with the same email will be updated.
        </p>
        {csvImportErrors.length > 0 && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <strong style={{ color: '#dc2626' }}>Errors ({csvImportErrors.length}):</strong>
            <ul style={{ margin: '6px 0 0 0', paddingLeft: 20 }}>
              {csvImportErrors.map((e, i) => (
                <li key={i} style={{ fontSize: 13, color: '#b91c1c' }}>
                  {e.row > 0 ? `Row ${e.row}: ` : ''}{e.error}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Table
          size="small"
          dataSource={csvImportRows.slice(0, 50).map((r, i) => ({ ...r, key: i }))}
          columns={[
            { title: '#', render: (_: any, __: any, idx: number) => idx + 1, width: 40 },
            { title: 'Name', dataIndex: 'name' },
            { title: 'Email', dataIndex: 'email' },
            { title: 'Phone', dataIndex: 'phone' },
            { title: 'Company', dataIndex: 'company_name' },
            { title: 'Type', dataIndex: 'customer_type' },
          ]}
          pagination={false}
          scroll={{ y: 280 }}
        />
        {csvImportRows.length > 50 && (
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>Showing first 50 of {csvImportRows.length} rows.</p>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button onClick={() => { setShowCsvImportModal(false); setCsvImportRows([]); setCsvImportErrors([]); }}>Cancel</Button>
          <Button
            type="primary"
            loading={csvImporting}
            disabled={csvImportRows.length === 0}
            onClick={submitCsvCustomerImport}
          >
            Import {csvImportRows.length} Customer{csvImportRows.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default CustomerList;